export interface ApplicationConfig {
	algoliaClientId?: string;
	datadogApplicationId?: string;
	datadogClientToken?: string;
	intercomApiBase?: string;
	intercomAppId?: string;
	monarchOrigin?: string;
	recognizeCable?: string;
	recognizeOrigin?: string;

	translationsOrigin?: string;
}

export type ApplicationEnv = 'development' | 'staging' | 'production' | 'test';

interface GetOptions {
	allowEmpty?: boolean;
	defaultValue?: string;
}

export class Application {
	public readonly env: string | null;

	constructor(
		env: ApplicationEnv | null = null,
		private config: ApplicationConfig = {}
	) {
		this.env = env;
	}

	isDevelopment() {
		return this.env === 'development';
	}

	isProduction() {
		return this.env === 'production';
	}

	isStaging() {
		return this.env === 'staging';
	}

	isTest() {
		return this.env === 'staging';
	}

	has(key: keyof ApplicationConfig): boolean {
		return key in this.config;
	}

	get(key: keyof ApplicationConfig, { allowEmpty = false, defaultValue = undefined }: GetOptions = {}): string {
		const value = this.config[key] ?? defaultValue;

		if (!allowEmpty && value === undefined) {
			throw new Error(`Config key "${key}" not found. Did you forget to include the key or wrap your component in an ApplicationProvider?`);
		}

		return value ?? '';
	}

	update(config: ApplicationConfig) {
		this.config = { ...this.config, ...config };
	}

	// Add getters for new ENV variables here to ensure proper typing
	get algoliaClientId() {
		return this.get('algoliaClientId');
	}

	get datadogApplicationId() {
		return this.get('datadogApplicationId');
	}

	get datadogClientToken() {
		return this.get('datadogClientToken');
	}

	get intercomApiBase() {
		return this.get('intercomApiBase', { allowEmpty: true });
	}

	get intercomAppId() {
		return this.get('intercomAppId', { allowEmpty: true });
	}

	get monarchOrigin() {
		return this.get('monarchOrigin');
	}

	get recognizeCable() {
		return this.get('recognizeCable');
	}

	get recognizeOrigin() {
		return this.get('recognizeOrigin');
	}

	get translationsOrigin() {
		return this.get('translationsOrigin');
	}
}
